import {
  Grid,
  Typography,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import { useState } from "react";
import { useTheme } from "@mui/material/styles";
import AssignmentLateRoundedIcon from "@mui/icons-material/AssignmentLateRounded";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Tooltip from "@mui/material/Tooltip";
import { uploadHistoricalLawsuits } from "../../services/LawsuitService";
import CardHeader from "../General/CardHeader";
import FileUploadBox from "../General/FileUploadBox";
import GlassCard from "../General/GlassCard";
import DynamicExcelHelpModal from "../General/DynamicExcelHelpModal";
import { useToast } from "../../context/ToastContext";

export default function UploadHistoricalLawsuit() {
  const { showToast } = useToast();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [openHelp, setOpenHelp] = useState(false);

  const handleFileSelect = (file) => {
    if (file) {
      const validExtensions = ["xls", "xlsx"];
      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (!validExtensions.includes(fileExtension)) {
        setErrors({ ...errors, file: "Solo se permiten archivos .xls o .xlsx" });
        setSelectedFile(null);
        return;
      }
      setErrors({ ...errors, file: "" });
      setSelectedFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!selectedFile) {
      showToast("Debe seleccionar un archivo.", "info");
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await uploadHistoricalLawsuits(formData);
      showToast(response.data.message, "success");
      setSelectedFile(null);
      setErrors({});
    } catch (error) {
      showToast(
        error.response?.data?.message || "Ocurrió un error inesperado.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 10 }}>
        <GlassCard>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Tooltip title="Ver requisitos del Excel" arrow>
              <HelpOutlineIcon
                sx={{
                  cursor: "pointer",
                  color: theme.palette.primary.main,
                  "&:hover": { color: theme.palette.primary.dark },
                }}
                onClick={() => setOpenHelp(true)}
              />
            </Tooltip>
          </Box>

          <CardHeader
            icon={<AssignmentLateRoundedIcon />}
            title="Cargar Títulos Históricos"
            description="Registrar títulos ya concluidos que nunca se cargaron al sistema, sin PDF adjunto. El abogado de cada título se toma del expediente vinculado (columna Código de Expediente), no se selecciona manualmente."
          />

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <FileUploadBox
                  selectedFile={selectedFile}
                  onFileSelect={handleFileSelect}
                  accept=".xls,.xlsx"
                  placeholder="Seleccionar Excel"
                  helperText="O arrastrá y soltá el archivo aquí (.xls, .xlsx)"
                />
                {errors.file && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ mt: 1, display: "block" }}
                  >
                    {errors.file}
                  </Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  fullWidth
                  disabled={loading}
                  sx={{ mt: 1 }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Cargar Títulos Históricos"
                  )}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </GlassCard>
      </Grid>

      <DynamicExcelHelpModal
        open={openHelp}
        onClose={() => setOpenHelp(false)}
        title="Requisitos del archivo Excel"
        description="El archivo debe ser formato .xls o .xlsx. Una fila por título. Todas las columnas son obligatorias, salvo la última. El Decreto debe incluir la barra (ej: 9736/2023); el número de Orden se genera automáticamente a partir de él, sin la barra. La contribución se calcula automáticamente a partir del tipo, igual que al leer un PDF. El Código de Expediente debe corresponder a un expediente ya cargado en el sistema y con un abogado asociado: de ahí sale el abogado del título. Si el expediente no existe o no tiene abogado, la fila se rechaza."
        columns={[
          "Tipo (Automotor / Inmueble / Comercio e Industria / Tasa Administrativa)",
          "Identificador",
          "Decreto (con barra, ej: 9736/2023)",
          "Monto",
          "Fecha (AAAA-MM-DD)",
          "DNI/CUIT",
          "Nombre",
          "Apellido",
          "Tipo de persona (F/J)",
          "Domicilio",
          "JUC ID",
          "Código de Expediente (define el abogado del título)",
          "Código del Estado del Sistema (opcional, ver Configuración → Estados del Sistema)",
        ]}
        example={[
          "Automotor",
          "AE059GH",
          "9736/2023",
          "71230.33",
          "2023-05-10",
          "20219473487",
          "CLAUDIO",
          "NEUSCHL",
          "F",
          "RICARDO ROJAS 8542",
          "JUC-12345",
          "2811730",
          "5",
        ]}
      />
    </Grid>
  );
}
